import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Spacer, VStack} from '@chakra-ui/react'

import {LogoSymbol} from '../Logo'
import NavTooltip from './NavTooltip'
import {productType, utilityActionType} from './types'

// The far-left rail. Top: the Marmalade mark + one icon per product the
// supplier is signed up to (the active product is tangerine). Bottom: utility
// actions, with sign out pinned to the very bottom.
const ProductRail = ({
  products,
  activeProductId,
  onProductChange,
  utilityActions,
  signOutAction,
  signOutIcon,
  authenticityToken,
  onSignOut,
}) => (
  <Flex
    direction="column"
    align="center"
    flexShrink={0}
    w="64px"
    h="100%"
    bg="mld.neutral.200"
    pb="20px"
  >
    {/* 64px header band so the logo lines up with the panel's header row
        (wordmark + collapse toggle). */}
    <Flex h="64px" align="center" justify="center" flexShrink={0}>
      <LogoSymbol width={25} height={20} />
    </Flex>

    {/* Product switcher icons only make sense with more than one product. */}
    {products.length > 1 && (
      <VStack spacing="12px">
        {products.map((product) => {
          const isActive = product.id === activeProductId
          return (
            <NavTooltip key={product.id} label={product.name}>
              <Box
                as="button"
                type="button"
                aria-label={product.name}
                aria-current={isActive ? 'true' : undefined}
                onClick={() => onProductChange(product.id)}
                p="10px"
                borderRadius="2px"
                lineHeight="0"
                color={isActive ? 'mld.primary.100' : 'mld.neutral.900'}
                _hover={{
                  color: isActive ? 'mld.primary.100' : 'mld.neutral.700',
                }}
              >
                {React.cloneElement(product.icon, {
                  boxSize: '28px',
                  filled: isActive,
                })}
              </Box>
            </NavTooltip>
          )
        })}
      </VStack>
    )}

    <Spacer />

    <VStack spacing="20px">
      {utilityActions.map((action) => (
        <NavTooltip key={action.key} label={action.label}>
          <Box
            as={action.href ? 'a' : 'button'}
            type={action.href ? undefined : 'button'}
            href={action.href}
            aria-label={action.label}
            onClick={action.onClick}
            position="relative"
            lineHeight="0"
            color="mld.neutral.900"
            _hover={{color: 'mld.neutral.700'}}
          >
            {React.cloneElement(action.icon, {boxSize: '24px'})}
            {action.hasNotification && (
              <Box
                position="absolute"
                top="-2px"
                right="-2px"
                w="8px"
                h="8px"
                borderRadius="full"
                bg="mld.primary.100"
              />
            )}
          </Box>
        </NavTooltip>
      ))}

      {signOutAction && signOutIcon && (
        <NavTooltip label="Sign out">
          <form method="POST" action={signOutAction}>
            <input
              type="hidden"
              name="authenticity_token"
              value={authenticityToken}
            />
            <input type="hidden" name="_method" value="delete" />
            <Box
              as="button"
              type="submit"
              aria-label="Sign out"
              onClick={onSignOut}
              lineHeight="0"
              color="mld.neutral.900"
              _hover={{color: 'mld.neutral.700'}}
            >
              {React.cloneElement(signOutIcon, {boxSize: '24px'})}
            </Box>
          </form>
        </NavTooltip>
      )}
    </VStack>
  </Flex>
)

ProductRail.propTypes = {
  products: PropTypes.arrayOf(productType).isRequired,
  activeProductId: PropTypes.string.isRequired,
  onProductChange: PropTypes.func,
  utilityActions: PropTypes.arrayOf(utilityActionType),
  signOutAction: PropTypes.string,
  signOutIcon: PropTypes.node,
  authenticityToken: PropTypes.string,
  onSignOut: PropTypes.func,
}

ProductRail.defaultProps = {
  onProductChange: () => {},
  utilityActions: [],
  signOutAction: undefined,
  signOutIcon: null,
  authenticityToken: '',
  onSignOut: () => {},
}

export default ProductRail
